import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  FileText,
  Receipt,
  CreditCard,
  RefreshCw,
  Mail,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { PORTAL } from "@/constants/testIds";
import { useLang } from "@/lib/useLang";
import { PortalHeader, PortalFooter } from "@/components/PortalChrome";
import { authApi } from "@/lib/api";
import { setPending } from "@/lib/pending";

/* -------------------------------------------------------------------------- */
/*  Sign-in panel — wired to POST /api/auth/request-code                      */
/* -------------------------------------------------------------------------- */
function SignInPanel({ t }) {
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading

  const btnBase =
    "w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-sm sm:text-[15px] font-bold tracking-wide uppercase transition-colors disabled:opacity-70 disabled:cursor-not-allowed";
  const btnStyle =
    "bg-[#C8A46B] hover:bg-[#B8925A] text-[#0B1930]";

  async function handleSubmit(e) {
    e.preventDefault();
    if (status === "loading") return;
    if (!value.trim()) {
      setError(t.signIn.errorEmpty);
      return;
    }
    setError("");
    setStatus("loading");
    try {
      const { data } = await authApi.requestCode(value.trim());
      setPending({
        pending_id: data.pending_id,
        contact_type: data.contact_type,
        contact_display_masked: data.contact_display_masked,
        resend_available_at: data.resend_available_at,
        original_input: value.trim(),
      });
      navigate("/verify");
    } catch (err) {
      setStatus("idle");
      if (err?.status === 429) {
        setError(err.detail);
      } else if (err?.status === 400) {
        setError(err.detail);
      } else {
        setError(
          err?.detail || "Something went wrong. Please try again in a moment.",
        );
      }
    }
  }

  return (
    <div className="flex flex-col">
      <div className="flex justify-center">
        <span
          className="flex h-16 w-16 items-center justify-center rounded-full bg-[#F0E9DC]"
          aria-hidden="true"
        >
          <User className="h-8 w-8 text-[#0B1930]" strokeWidth={1.75} />
        </span>
      </div>
      <h2
        className="mt-5 text-center font-heading uppercase tracking-[0.02em] text-[#0B1930] text-2xl sm:text-3xl"
        data-testid={PORTAL.signInHeading}
      >
        {t.signIn.title}
      </h2>
      <p className="mt-2 text-center text-[15px] text-[#4B5563]">
        {t.signIn.description}
      </p>
      <form
        onSubmit={handleSubmit}
        className="mt-7 flex flex-col gap-2"
        noValidate
      >
        <label
          htmlFor={PORTAL.signInInput}
          className="text-sm font-medium text-[#0B1930]"
        >
          {t.signIn.inputLabel}
        </label>
        <div
          className={`group relative flex items-center rounded-xl border bg-white transition-colors ${
            error
              ? "border-red-400 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-100"
              : "border-[#E6E0D1] focus-within:border-[#C8A46B] focus-within:ring-2 focus-within:ring-[#C8A46B]/25"
          }`}
        >
          <span className="pl-4 pr-2 text-[#B8925A]">
            <Mail className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <input
            id={PORTAL.signInInput}
            data-testid={PORTAL.signInInput}
            type="text"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError("");
            }}
            placeholder={t.signIn.inputPlaceholder}
            className="flex-1 bg-transparent py-3.5 pr-4 text-[15px] text-[#0B1930] placeholder:text-[#9AA1AC] outline-none"
            aria-invalid={!!error}
            aria-describedby={error ? PORTAL.signInInputError : undefined}
            autoComplete="username email tel"
            inputMode="email"
          />
        </div>
        {error && (
          <p
            id={PORTAL.signInInputError}
            data-testid={PORTAL.signInInputError}
            className="flex items-center gap-1.5 text-xs font-medium text-red-600"
            role="alert"
            aria-live="polite"
          >
            <AlertCircle className="h-3.5 w-3.5" />
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={status === "loading"}
          className={`${btnBase} ${btnStyle} mt-4`}
          data-testid={PORTAL.signInSubmitBtn}
        >
          {status === "loading" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t.signIn.submitting}
            </>
          ) : (
            t.signIn.submit
          )}
        </button>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Pay-invoice panel — Phase 1 simulated behavior (unchanged)                */
/* -------------------------------------------------------------------------- */
function PayInvoicePanel({ t }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle");

  const btnBase =
    "w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-sm sm:text-[15px] font-bold tracking-wide uppercase transition-colors disabled:opacity-70 disabled:cursor-not-allowed";
  const btnStyle = "bg-[#0B1930] hover:bg-[#162B4D] text-white";

  function handleSubmit(e) {
    e.preventDefault();
    if (status === "loading") return;
    if (!value.trim()) {
      setError(t.pay.errorEmpty);
      return;
    }
    setError("");
    setStatus("loading");
    setTimeout(() => setStatus("success"), 1100);
  }

  function reset() {
    setStatus("idle");
    setValue("");
    setError("");
  }

  if (status === "success") {
    return (
      <div
        className="flex flex-col items-start gap-4 py-4"
        data-testid={PORTAL.paySuccess}
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F0E9DC]">
          <CheckCircle2 className="h-7 w-7 text-[#0B1930]" strokeWidth={2.2} />
        </span>
        <h3 className="font-heading text-2xl sm:text-[26px] uppercase tracking-wide text-[#0B1930]">
          {t.pay.successTitle}
        </h3>
        <p className="text-[15px] leading-relaxed text-[#4B5563] max-w-sm">
          {t.pay.successBody}
        </p>
        <button
          type="button"
          onClick={reset}
          className="mt-1 text-sm font-semibold text-[#B8925A] hover:text-[#0B1930] transition-colors underline-offset-4 hover:underline"
          data-testid={`${PORTAL.paySuccess}-action`}
        >
          {t.pay.successAction} →
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex justify-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#F0E9DC]">
          <FileText className="h-8 w-8 text-[#0B1930]" strokeWidth={1.75} />
        </span>
      </div>
      <h2
        className="mt-5 text-center font-heading uppercase tracking-[0.02em] text-[#0B1930] text-2xl sm:text-3xl"
        data-testid={PORTAL.payHeading}
      >
        {t.pay.title}
      </h2>
      <p className="mt-2 text-center text-[15px] text-[#4B5563]">
        {t.pay.description}
      </p>
      <form
        onSubmit={handleSubmit}
        className="mt-7 flex flex-col gap-2"
        noValidate
      >
        <label
          htmlFor={PORTAL.payInput}
          className="text-sm font-medium text-[#0B1930]"
        >
          {t.pay.inputLabel}
        </label>
        <div
          className={`group relative flex items-center rounded-xl border bg-white transition-colors ${
            error
              ? "border-red-400 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-100"
              : "border-[#E6E0D1] focus-within:border-[#C8A46B] focus-within:ring-2 focus-within:ring-[#C8A46B]/25"
          }`}
        >
          <span className="pl-4 pr-2 text-[#B8925A]">
            <FileText className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <input
            id={PORTAL.payInput}
            data-testid={PORTAL.payInput}
            type="text"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError("");
            }}
            placeholder={t.pay.inputPlaceholder}
            className="flex-1 bg-transparent py-3.5 pr-4 text-[15px] text-[#0B1930] placeholder:text-[#9AA1AC] outline-none"
            aria-invalid={!!error}
            aria-describedby={error ? PORTAL.payInputError : undefined}
            autoComplete="off"
          />
        </div>
        {error && (
          <p
            id={PORTAL.payInputError}
            data-testid={PORTAL.payInputError}
            className="flex items-center gap-1.5 text-xs font-medium text-red-600"
            role="alert"
            aria-live="polite"
          >
            <AlertCircle className="h-3.5 w-3.5" />
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={status === "loading"}
          className={`${btnBase} ${btnStyle} mt-4`}
          data-testid={PORTAL.paySubmitBtn}
        >
          {status === "loading" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t.pay.submitting}
            </>
          ) : (
            t.pay.submit
          )}
        </button>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Benefit card                                                              */
/* -------------------------------------------------------------------------- */
function BenefitCard({ icon: Icon, title, body, testId }) {
  return (
    <div
      className="group relative flex flex-col rounded-2xl bg-white p-6 sm:p-7 border border-[#EDE5D4] shadow-[0_4px_24px_rgba(11,25,48,0.04)] hover:shadow-[0_10px_30px_rgba(11,25,48,0.08)] transition-shadow"
      data-testid={testId}
    >
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F0E9DC] mb-4">
        <Icon className="h-6 w-6 text-[#0B1930]" strokeWidth={1.75} />
      </span>
      <h3 className="font-heading uppercase tracking-wide text-[#0B1930] text-lg sm:text-xl">
        {title}
      </h3>
      <p className="mt-1.5 text-[14.5px] leading-relaxed text-[#4B5563]">
        {body}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */
export default function PortalLanding() {
  const { lang, t, toggle } = useLang();

  return (
    <div
      className="min-h-screen flex flex-col zmex-page-bg"
      data-testid={PORTAL.page}
    >
      <PortalHeader t={t} lang={lang} onToggleLang={toggle} />

      <main className="relative flex-1">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-12 sm:pt-16 lg:pt-20 pb-16 sm:pb-20">
          <div className="max-w-3xl mx-auto text-center">
            <h1
              className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.05]"
              style={{
                fontFamily: '"DM Sans", "Inter", sans-serif',
                backgroundImage:
                  "linear-gradient(180deg, #2f4370 0%, #0B1930 45%, #050d1c 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                WebkitTextFillColor: "transparent",
                filter:
                  "drop-shadow(0 1px 0 rgba(255,255,255,0.55)) drop-shadow(0 3px 8px rgba(11,25,48,0.18))",
              }}
              data-testid={PORTAL.heroHeading}
            >
              {t.hero.heading}
            </h1>
            <p
              className="mt-5 sm:mt-6 text-base sm:text-lg text-[#4B5563] max-w-2xl mx-auto leading-relaxed"
              data-testid={PORTAL.heroSubtitle}
            >
              {t.hero.subtitle}
            </p>
          </div>

          <div className="mt-10 sm:mt-14 max-w-4xl mx-auto">
            <div className="relative overflow-hidden rounded-3xl bg-white border border-[#EDE5D4] shadow-[0_12px_40px_rgba(11,25,48,0.06)]">
              <div className="grid grid-cols-1 md:grid-cols-2 md:divide-x md:divide-[#EDE5D4]">
                <div className="p-6 sm:p-10" data-testid={PORTAL.signInCard}>
                  <SignInPanel t={t} />
                </div>
                <div
                  className="p-6 sm:p-10 border-t md:border-t-0 border-[#EDE5D4]"
                  data-testid={PORTAL.payCard}
                >
                  <PayInvoicePanel t={t} />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-14 sm:mt-20 max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <BenefitCard
              icon={Receipt}
              title={t.benefits.viewInvoices.title}
              body={t.benefits.viewInvoices.body}
              testId={PORTAL.benefitViewInvoices}
            />
            <BenefitCard
              icon={CreditCard}
              title={t.benefits.makePayments.title}
              body={t.benefits.makePayments.body}
              testId={PORTAL.benefitMakePayments}
            />
            <BenefitCard
              icon={RefreshCw}
              title={t.benefits.manageAutopay.title}
              body={t.benefits.manageAutopay.body}
              testId={PORTAL.benefitManageAutopay}
            />
          </div>
        </div>
      </main>

      <PortalFooter t={t} />
    </div>
  );
}
